import classNames from "classnames";
import {useFieldArray, useFormContext} from "react-hook-form";
import {FiChevronsRight, FiPlus, FiX} from "react-icons/fi";

import {Button} from "@/components/button/Button.tsx";
import {Card} from "@/components/card/Card.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {ContactsSelectField} from "@/components/form/fields/ContactsSelectField/ContactsSelectField.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {Input} from "@/components/input/Input.tsx";
import {ServiceField} from "@/lib/constants/service.ts";
import {ContactGroupBase} from "@/lib/types/contacts.ts";
import {isNumeric} from "@/lib/validators.tsx";
import {CheckFormInfo} from "@/pages/checks/components/form/CheckFormInfo.tsx";
import {DurationField} from "@/pages/checks/components/form/fields/DurationField.tsx";

import styles from "./CheckEscalationForm.module.scss";

interface EscalationInput {
  wait_time: number;
  num_repeats: number;
  contact_groups: number[];
}

export interface CheckEscalationFormValues {
  escalations: EscalationInput[];
}

interface CheckEscalationFormProps {
  className?: string;
  contactGroups?: ContactGroupBase[];
  addDefaultRowOnEmpty?: boolean;
}

export const CheckEscalationForm = ({className, contactGroups, addDefaultRowOnEmpty}: CheckEscalationFormProps) => {
  const name = ServiceField.ESCALATIONS;
  const {
    control,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: {errors},
  } = useFormContext();
  const {fields, append, remove} = useFieldArray({
    control,
    name,
  });
  if (fields.length === 0 && addDefaultRowOnEmpty) {
    append({num_repeats: 0, contact_groups: [], wait_time: 0});
  }

  const escalationError = errors?.escalations?.message?.toString();
  return (
    <div className={className}>
      <div className="mb-3">
        <CheckFormInfo title="Escalations let you notify additional contacts if a Check stays down for a longer period of time." />
        <div className="pulseTextBody mt-2">
          Note: Existing escalations will be replaced with the parameters set below.
        </div>
      </div>
      <Card className="pulseMaxWidthXL">
        <div className="container p-0 mx-0">
          <div className="d-none d-md-block">
            <div className={classNames(styles.escalationTableTitle, "pulseTextBodySmallBold row gy-3 gx-4")}>
              <div className="col-5">After:</div>
              <div className="col-5">Notify:</div>
              <div className="col-2 text-nowrap">Repeat Times:</div>
            </div>
            <Divider className="my-3" />
          </div>
          {fields.map((f, index) => (
            <div key={f.id}>
              <div className="d-flex gap-3 flex-column flex-md-row">
                <div className="flex-grow-1 row gy-3 gx-4">
                  <div className="col-12 col-md-4">
                    <div className="pulseTextBodySmallBold d-md-none d-block">
                      After:
                      <Divider className="my-3" />
                    </div>
                    <DurationField name={`${name}.${index}.wait_time`} />
                  </div>
                  <div className="col-1 d-none d-md-block">
                    <Icon icon={FiChevronsRight} className="my-2 mx-auto" />
                  </div>
                  <div className="col-12 col-md-5">
                    <div className="pulseTextBodySmallBold d-md-none d-block">
                      Notify:
                      <Divider className="my-3" />
                    </div>
                    <ContactsSelectField
                      contacts={contactGroups}
                      name={`${name}.${index}.contact_groups`}
                      label=""
                      onChange={(ids) => {
                        if (escalationError) {
                          clearErrors(name);
                        }
                        setValue(`${name}.${index}.contact_groups`, ids, {shouldDirty: true, shouldValidate: true});
                      }}
                      menuPortalTarget={
                        (document.querySelector(`div.outlet-container > div.content-container`) as HTMLElement) ??
                        document.body
                      }
                      menuPosition={"fixed"}
                    />
                  </div>
                  <div className="col-12 col-md-2">
                    <div className="pulseTextBodySmallBold d-md-none d-block">
                      Repeat Times:
                      <Divider className="my-3" />
                    </div>
                    <InputField
                      name={`${name}.${index}.num_repeats`}
                      input={
                        <Input
                          type="text"
                          maxLength={5}
                          value={watch(`${name}.${index}.num_repeats`)}
                          onChange={({target}) => {
                            if (escalationError) {
                              clearErrors(name);
                            }

                            const input = target.value;
                            const field = `${name}.${index}.num_repeats`;
                            const message = "Repeat Times must be a number between 0 and 5000.";

                            if (!input || !isNumeric(input)) {
                              setValue(field, "", {shouldDirty: true});
                              setError(field, {message});
                              return;
                            }

                            const parsedInput = parseInt(input);
                            setValue(field, parsedInput, {shouldDirty: true});

                            if (parsedInput < 0 || parsedInput > 5000) {
                              setError(field, {message});
                            } else {
                              clearErrors(field);
                            }
                          }}
                        />
                      }
                    />
                  </div>
                </div>
                <Button
                  onClick={() => {
                    clearErrors(escalationError ? name : `${name}.${index}`);
                    remove(index);
                  }}
                  variant="ghost"
                  size="fitContent"
                >
                  <Icon icon={FiX} className="m-md-2" />
                  <span className="d-md-none d-inline-block">Clear Rule</span>
                </Button>
              </div>
              <div>
                <Divider className="my-3" />
              </div>
            </div>
          ))}
          <div className="mt-md-3 m-0">
            <Button variant="secondary" onClick={() => append({num_repeats: 0, contact_groups: [], wait_time: 0})}>
              <Icon icon={FiPlus} /> Add Another
            </Button>
          </div>
        </div>
      </Card>
      {escalationError && (
        <div className="mt-4">
          <FormErrorAlerts errors={[escalationError]} />
        </div>
      )}
    </div>
  );
};
